import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { commissionReportsService, getErrorMessage } from '@/services';
import type { ReportFilters } from '@/services/commissionReports';

// ─── Blob download helper ──────────────────────────────────────────────────

function downloadBlob(blob: Blob, prefix: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${prefix}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── By Insurer ─────────────────────────────────────────────────────────────

export function useByInsurerReport(page = 1, filters?: ReportFilters) {
  return useQuery({
    queryKey: ['byInsurerReport', page, filters],
    queryFn: () => commissionReportsService.getByInsurer(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useExportByInsurer() {
  return useMutation({
    mutationFn: (filters?: ReportFilters) =>
      commissionReportsService.exportByInsurer(filters),
    onSuccess: (blob) => {
      downloadBlob(blob, 'commission-by-insurer');
      toast.success('Report exported successfully');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── By Insurer → drill-down (one insurer's policies) ───────────────────────

export function usePoliciesByInsurer(insurerId: string, page = 1, filters?: ReportFilters) {
  return useQuery({
    queryKey: ['insurerPolicies', insurerId, page, filters],
    queryFn: () => commissionReportsService.getPoliciesByInsurer(insurerId, page, filters),
    enabled: Boolean(insurerId),
    placeholderData: (prev) => prev,
  });
}

export function useExportPoliciesByInsurer() {
  return useMutation({
    mutationFn: ({ insurerId, filters }: { insurerId: string; filters?: ReportFilters }) =>
      commissionReportsService.exportPoliciesByInsurer(insurerId, filters),
    onSuccess: (blob) => {
      downloadBlob(blob, 'policies-by-insurer');
      toast.success('Report exported successfully');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── By POSP → drill-down (one agent's policies) ────────────────────────────
// Reuses GET /brokers/posps/{id}/policies/ (see commissionReportsService.exportPoliciesByPosp).

export function useExportPoliciesByPosp() {
  return useMutation({
    mutationFn: ({ pospId, filters }: { pospId: string; filters?: ReportFilters }) =>
      commissionReportsService.exportPoliciesByPosp(pospId, filters),
    onSuccess: (blob) => {
      downloadBlob(blob, 'policies-by-posp');
      toast.success('Report exported successfully');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── By Product ─────────────────────────────────────────────────────────────

export function useByProductReport(page = 1, filters?: ReportFilters) {
  return useQuery({
    queryKey: ['byProductReport', page, filters],
    queryFn: () => commissionReportsService.getByProduct(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useExportByProduct() {
  return useMutation({
    mutationFn: (filters?: ReportFilters) =>
      commissionReportsService.exportByProduct(filters),
    onSuccess: (blob) => {
      downloadBlob(blob, 'commission-by-product');
      toast.success('Report exported successfully');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── By POSP (Agent) ──────────────────────────────────────────────────────────

export function useByPospReport(page = 1, filters?: ReportFilters) {
  return useQuery({
    queryKey: ['byPospReport', page, filters],
    queryFn: () => commissionReportsService.getByPosp(page, filters),
    placeholderData: (prev) => prev,
  });
}

export function useExportByPosp() {
  return useMutation({
    mutationFn: (filters?: ReportFilters) =>
      commissionReportsService.exportByPosp(filters),
    onSuccess: (blob) => {
      downloadBlob(blob, 'commission-by-posp');
      toast.success('Report exported successfully');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── Outstanding owed per POSP (item 7 step 3) ──────────────────────────────

export function usePospOutstanding(filters?: ReportFilters) {
  return useQuery({
    queryKey: ['pospOutstanding', filters],
    queryFn: () => commissionReportsService.getPospOutstanding(filters),
    placeholderData: (prev) => prev,
  });
}

// ─── Lifecycle + Payout Register ────────────────────────────────────────────

export function useLifecyclePipelineReport(filters?: ReportFilters) {
  return useQuery({
    queryKey: ['lifecyclePipelineReport', filters],
    queryFn: () => commissionReportsService.getLifecyclePipeline(filters),
  });
}

export function useExportLifecyclePipeline() {
  return useMutation({
    mutationFn: (filters?: ReportFilters) =>
      commissionReportsService.exportLifecyclePipeline(filters),
    onSuccess: (blob) => {
      downloadBlob(blob, 'commission-lifecycle');
      toast.success('Report exported successfully');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── Profitability (broker_admin only) ──────────────────────────────────────
// `enabled` is passed by the caller (ReportsTab.tsx) so the request is never
// even issued for a role the backend would 403 — see visibility gate there.

export function useProfitabilityReport(filters?: ReportFilters, enabled = true) {
  return useQuery({
    queryKey: ['profitabilityReport', filters],
    queryFn: () => commissionReportsService.getProfitability(filters),
    enabled,
  });
}
